import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import AiJobMatchCard from '../components/AiJobMatchCard'
import JobShareButton from '../components/JobShareButton'
import { getJobDetailUrl, isCareersRoute } from './careersRoutes'
import { getAPIBaseURL } from '../services/helpers'
import { getData, postData } from '../services/request'
import { employmentTypeLabel, locationTypeLabel } from '../utils/employmeLabels'

const JobListPage = () => {
  const location = useLocation()
  const publicCatalog = isCareersRoute(location.pathname)
  const jobLink = (jobId) => getJobDetailUrl(jobId, location.pathname)
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [applications, setApplications] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('')
  const [locationTypeFilter, setLocationTypeFilter] = useState('')
  const [aiMatches, setAiMatches] = useState([])
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState('')

  const isLoggedIn =
    typeof window !== 'undefined' &&
    (sessionStorage.getItem('employme-active') === '1' || !!localStorage.getItem('employmeUser'))

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true)
      const url = `${getAPIBaseURL()}/employme/jobs`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (reqError || !result) {
        setError('Unable to load jobs')
        return
      }
      setJobs(result.data.data || [])
    }
    fetchJobs()
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return
    const fetchApplications = async () => {
      const url = `${getAPIBaseURL()}/employme/jobseeker/applications`
      const { error: reqError, result } = await getData(url, {}, {})
      if (reqError || !result) {
        return
      }
      setApplications(result.data.data || [])
    }
    fetchApplications()
  }, [isLoggedIn])

  const appliedJobIds = useMemo(() => {
    const ids = new Set()
    applications.forEach((app) => {
      const id = app.jobId?._id || app.jobId
      if (id) {
        ids.add(id.toString())
      }
    })
    return ids
  }, [applications])

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const inTitle = job.title?.toLowerCase().includes(term)
        const inDesc = job.description?.toLowerCase().includes(term)
        const inCompany = job.companyName?.toLowerCase().includes(term)
        if (!inTitle && !inDesc && !inCompany) {
          return false
        }
      }

      if (locationFilter) {
        const loc = `${job.location?.city || ''} ${job.location?.state || ''}`.toLowerCase()
        if (!loc.includes(locationFilter.toLowerCase())) {
          return false
        }
      }

      if (employmentTypeFilter && job.employmentType !== employmentTypeFilter) {
        return false
      }

      if (locationTypeFilter && job.locationType !== locationTypeFilter) {
        return false
      }

      return true
    })
  }, [jobs, searchTerm, locationFilter, employmentTypeFilter, locationTypeFilter])

  const employmentTypes = useMemo(() => {
    const set = new Set()
    jobs.forEach((job) => {
      if (job.employmentType) {
        set.add(job.employmentType)
      }
    })
    return Array.from(set)
  }, [jobs])

  const locationTypes = useMemo(() => {
    const set = new Set()
    jobs.forEach((job) => {
      if (job.locationType) {
        set.add(job.locationType)
      }
    })
    return Array.from(set)
  }, [jobs])

  const fetchAiMatches = async () => {
    setAiError('')
    setAiLoading(true)
    setAiMatches([])
    const url = `${getAPIBaseURL()}/employme/jobseeker/ai/job-matches`
    const { error: reqError, result } = await postData(
      url,
      { 'Content-Type': 'application/json' },
      JSON.stringify({})
    )
    setAiLoading(false)
    if (reqError || !result) {
      setAiError(result?.data?.data || 'Unable to load AI job matches right now.')
      return
    }
    if (result.status === 200) {
      const recs = result.data?.data?.recommendations || []
      setAiMatches(recs)
      if (!recs.length) {
        setAiError('No strong matches found yet. Try updating your profile or checking back later.')
      }
    } else {
      setAiError(result.data?.data || 'Unable to load AI job matches right now.')
    }
  }

  return (
    <div className={publicCatalog ? '' : 'max-w-6xl mx-auto'}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
            {publicCatalog ? 'Open job opportunities' : 'Jobs'}
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            {publicCatalog
              ? 'Browse live roles from employers. No account required to view job posts.'
              : 'Filter roles by location and type, and search for titles that match your skills.'}
          </p>
        </div>
      </div>

      {isLoggedIn && !publicCatalog && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-[#00A5E6]">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900">Smart job matches</h2>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                  Get personalised role suggestions based on your profile.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchAiMatches}
              disabled={aiLoading}
              className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-[#00A5E6] px-4 py-2.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-[#008FCC] disabled:opacity-60 sm:w-auto"
            >
              {aiLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Finding matches…
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Get matches
                </>
              )}
            </button>
          </div>

          {aiError && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {aiError}
            </div>
          )}
          {!aiLoading && !aiError && aiMatches.length === 0 && (
            <p className="mt-3 text-xs text-slate-500">
              No suggestions yet. Complete your profile, then try again.
            </p>
          )}
          {aiMatches.length > 0 && (
            <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {aiMatches.map((item) => (
                <AiJobMatchCard key={item.job?._id || item.jobId} item={item} compact />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 rounded-lg bg-white border border-slate-200 p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by job title, company or keywords"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
            <input
              type="text"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="City or state"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Employment type</label>
              <select
                value={employmentTypeFilter}
                onChange={(e) => setEmploymentTypeFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-600"
              >
                <option value="">Any</option>
                {employmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {employmentTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Location type</label>
              <select
                value={locationTypeFilter}
                onChange={(e) => setLocationTypeFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-600"
              >
                <option value="">Any</option>
                {locationTypes.map((type) => (
                  <option key={type} value={type}>
                    {locationTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
      {loading && <div className="text-sm text-slate-600">Loading jobs...</div>}
      {!loading && filteredJobs.length === 0 && (
        <div className="text-sm text-slate-600">No jobs match your filters yet.</div>
      )}
      <div className="grid gap-4 mt-4">
        {filteredJobs.map(job => {
          const isApplied = appliedJobIds.has(job._id?.toString())
          return (
            <div
              key={job._id}
              className="border rounded-lg p-4 bg-white hover:border-blue-200 hover:shadow-sm"
            >
              <div className="flex justify-between items-start gap-4">
                <div>
                  <Link to={jobLink(job._id)}>
                    <h2 className="text-lg font-medium text-slate-900 hover:text-blue-700">{job.title}</h2>
                  </Link>
                  <p className="text-xs text-slate-500 mt-1">
                    {job.location?.city}, {job.location?.state} • {locationTypeLabel(job.locationType)}
                  </p>
                  {isApplied && (
                    <p className="mt-1 inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                      Applied
                    </p>
                  )}
                </div>
                <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs">
                  {employmentTypeLabel(job.employmentType)}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-700 line-clamp-2">
                {job.description}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Link
                  to={jobLink(job._id)}
                  className="text-sm font-medium text-blue-700 hover:text-blue-800"
                >
                  View job
                </Link>
                <JobShareButton jobId={job._id} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default JobListPage

